import { useEffect, useRef, useState } from "react";
import { X, Search, Users, Camera, Trash2, Copy, Check, RotateCcw, Shield, ShieldOff, UserPlus, UserMinus, Crown, LogOut, Link2 } from "lucide-react";
import { api, uploadImage } from "../api";
import { Avatar } from "./cards";
import { mediaUrl } from "../config";

const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60";

export const Modal = ({ children, onClose, testId, wide }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm" onClick={onClose} data-testid={testId}>
    <div className={`w-full ${wide ? "max-w-lg" : "max-w-md"} max-h-[85vh] overflow-y-auto rounded-3xl border border-white/10 bg-surface p-6`} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

export const NewChatModal = ({ onClose, onOpen }) => {
  const [tab, setTab] = useState("dm");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      if (!q.trim()) return setResults([]);
      api
        .get(`/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setResults(r.creators || r.users || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const startDm = async (u) => {
    setBusy(true);
    setError("");
    try {
      const conv = await api.post("/conversations/dm", { userId: u.id });
      onOpen(conv.id || conv.conversationId);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selected.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const conv = await api.post("/conversations/group", {
        name: groupName.trim(),
        title: groupName.trim(),
        memberIds: selected.map((u) => u.id),
      });
      onOpen(conv.id || conv.conversationId);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const toggleMember = (u) =>
    setSelected((s) => (s.some((x) => x.id === u.id) ? s.filter((x) => x.id !== u.id) : [...s, u]));

  return (
    <Modal onClose={onClose} testId="new-chat-modal">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-white">New conversation</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="new-chat-close"><X size={18} /></button>
      </div>
      <div className="mb-4 grid grid-cols-2 rounded-full border border-white/10 bg-white/[0.03] p-1">
        {[["dm", "Direct message"], ["group", "New group"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`new-chat-tab-${k}`} className={`rounded-full py-2 font-heading text-sm font-semibold transition-colors ${tab === k ? "bg-crimson text-white" : "text-slate-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "group" && (
        <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" className={`${inputCls} mb-3`} data-testid="group-name-input" />
      )}
      {tab === "group" && selected.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {selected.map((u) => (
            <button key={u.id} onClick={() => toggleMember(u)} className="flex items-center gap-1.5 rounded-full bg-crimson/15 px-3 py-1 text-xs font-semibold text-crimson" data-testid={`selected-member-${u.id}`}>
              {u.displayName || u.username} <X size={11} />
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5">
        <Search size={15} className="text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className="w-full bg-transparent text-sm text-white outline-none placeholder-slate-600" data-testid="new-chat-search" />
      </div>
      <div className="mt-3 max-h-64 space-y-1 overflow-y-auto">
        {results.map((u) => (
          <div key={u.id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/[0.04]" data-testid={`person-${u.id}`}>
            <Avatar user={u} size={36} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-sm font-semibold text-white">{u.displayName || u.username}</p>
              <p className="truncate text-xs text-slate-500">@{u.username}</p>
            </div>
            {tab === "dm" ? (
              <button onClick={() => startDm(u)} disabled={busy} className="rounded-full bg-crimson px-4 py-1.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover disabled:opacity-50" data-testid={`message-user-${u.id}`}>
                Message
              </button>
            ) : (
              <button onClick={() => toggleMember(u)} className={`rounded-full px-4 py-1.5 font-heading text-xs font-bold transition-colors ${selected.some((x) => x.id === u.id) ? "bg-crimson text-white" : "border border-white/15 text-slate-200"}`} data-testid={`add-member-${u.id}`}>
                {selected.some((x) => x.id === u.id) ? "Added" : "Add"}
              </button>
            )}
          </div>
        ))}
        {q.trim() && results.length === 0 && <p className="py-6 text-center text-sm text-slate-600">No people found.</p>}
      </div>
      {error && <p className="mt-3 text-sm text-crimson">{error}</p>}
      {tab === "group" && (
        <button onClick={createGroup} disabled={busy || !groupName.trim() || selected.length === 0} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white hover:bg-crimson-hover disabled:opacity-50" data-testid="create-group-button">
          <Users size={15} /> Create group ({selected.length} member{selected.length === 1 ? "" : "s"})
        </button>
      )}
    </Modal>
  );
};

const Toggle = ({ label, desc, value, onChange, disabled, testId }) => (
  <button
    onClick={() => !disabled && onChange(!value)}
    disabled={disabled}
    className={`flex w-full items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3.5 text-left transition-colors ${disabled ? "opacity-50" : "hover:border-white/25"}`}
    data-testid={testId}
  >
    <span>
      <span className="block font-heading text-sm font-semibold text-white">{label}</span>
      {desc && <span className="mt-0.5 block text-xs text-slate-500">{desc}</span>}
    </span>
    <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${value ? "bg-crimson" : "bg-white/15"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
    </span>
  </button>
);

export const GroupSettingsModal = ({ conv, myId, onClose, onChanged }) => {
  const iAmAdmin = conv.isAdmin || (conv.admins || []).includes(myId) || conv.createdBy === myId;
  const [name, setName] = useState(conv.name || conv.title || "");
  const [joinRequests, setJoinRequests] = useState([]);
  const [addQ, setAddQ] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef(null);
  const inviteLink = conv.inviteCode ? `${window.location.origin}/app/join/${conv.inviteCode}` : null;

  useEffect(() => {
    if (iAmAdmin && (conv.joinRequestCount || 0) > 0) {
      api.get(`/conversations/${conv.id}/join-requests`).then((r) => setJoinRequests(Array.isArray(r) ? r : r.requests || r.users || [])).catch(() => {});
    }
  }, [conv.id, conv.joinRequestCount, iAmAdmin]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!addQ.trim()) return setAddResults([]);
      api.get(`/search?q=${encodeURIComponent(addQ.trim())}`).then((r) => setAddResults(r.creators || r.users || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [addQ]);

  const patch = async (body) => {
    setError("");
    try {
      await api.patch(`/conversations/${conv.id}`, body);
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const call = (p, body) => async () => {
    setError("");
    try {
      await api.post(p, body);
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const changePicture = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const key = await uploadImage(file, "chat");
      await patch({ avatarKey: key });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const decide = (uid, decision) => async () => {
    try {
      await api.post(`/conversations/${conv.id}/join-requests/${uid}/${decision}`);
      setJoinRequests((l) => l.filter((r) => (r.id || r.userId) !== uid));
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const members = conv.members || [];
  const showMembers = iAmAdmin || !conv.onlyAdminsSeeMembers;

  return (
    <Modal onClose={onClose} testId="group-settings-modal" wide>
      <div className="mb-5 flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-white">Group settings</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="group-settings-close"><X size={18} /></button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative">
          {mediaUrl(conv.avatar) ? (
            <img src={mediaUrl(conv.avatar)} alt="" className="h-16 w-16 rounded-2xl object-cover" data-testid="group-avatar" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-crimson/20 text-crimson" data-testid="group-avatar">
              <Users size={24} />
            </div>
          )}
        </div>
        {iAmAdmin && (
          <div className="flex gap-2">
            <button onClick={() => fileInput.current?.click()} disabled={busy} className="flex items-center gap-2 rounded-full bg-crimson px-4 py-2 font-heading text-xs font-bold text-white hover:bg-crimson-hover disabled:opacity-50" data-testid="group-change-picture">
              <Camera size={13} /> {conv.avatar ? "Change picture" : "Add picture"}
            </button>
            {conv.avatar && (
              <button onClick={() => patch({ avatarKey: null, avatar: null })} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-heading text-xs font-bold text-slate-200 hover:border-crimson/50" data-testid="group-delete-picture">
                <Trash2 size={13} /> Remove
              </button>
            )}
            <input ref={fileInput} type="file" accept="image/*" className="hidden" data-testid="group-picture-input" onChange={(e) => changePicture(e.target.files?.[0])} />
          </div>
        )}
      </div>

      {iAmAdmin && (
        <div className="mt-4 flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="group-rename-input" />
          <button onClick={() => patch({ name, title: name })} className="shrink-0 rounded-xl bg-white/10 px-4 font-heading text-xs font-bold text-white hover:bg-white/20" data-testid="group-rename-save">
            Save
          </button>
        </div>
      )}

      {inviteLink && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Group link</p>
          <div className="mt-2 flex items-center gap-2">
            <p className="min-w-0 flex-1 truncate rounded-xl bg-black/40 px-3 py-2 font-mono text-xs text-slate-300" data-testid="group-invite-link">{inviteLink}</p>
            <button onClick={copyLink} className="rounded-xl bg-white/10 p-2.5 text-white hover:bg-white/20" title="Copy link" data-testid="group-copy-link">
              {copied ? <Check size={15} className="text-emerald-400" /> : <Copy size={15} />}
            </button>
            {iAmAdmin && (
              <button onClick={call(`/conversations/${conv.id}/invite/reset`)} className="rounded-xl bg-white/10 p-2.5 text-white hover:bg-white/20" title="Reset link" data-testid="group-reset-link">
                <RotateCcw size={15} />
              </button>
            )}
          </div>
        </div>
      )}

      {iAmAdmin && (
        <div className="mt-5 space-y-2.5">
          <Toggle label="Lock chat" desc="Only admins can send messages" value={!!conv.onlyAdminsSend} onChange={(v) => patch({ onlyAdminsSend: v })} testId="toggle-only-admins-send" />
          <Toggle label="Hide member list" desc="Only admins can see who is in the group" value={!!conv.onlyAdminsSeeMembers} onChange={(v) => patch({ onlyAdminsSeeMembers: v })} testId="toggle-hide-members" />
          <Toggle label="Approve new members" desc="Every new member needs an admin's approval to join" value={!!conv.requireApproval} onChange={(v) => patch({ requireApproval: v })} testId="toggle-require-approval" />
          <Toggle label="Public group" desc="Anyone with the link can join (no approval needed)" value={!!conv.isPublic} onChange={(v) => patch({ isPublic: v })} testId="toggle-public" />
        </div>
      )}

      {iAmAdmin && joinRequests.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 font-heading text-sm font-bold text-white">Join requests</p>
          <div className="space-y-2" data-testid="join-requests-list">
            {joinRequests.map((r) => {
              const uid = r.id || r.userId;
              return (
                <div key={uid} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5" data-testid={`join-request-${uid}`}>
                  <Avatar user={r} size={32} />
                  <p className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{r.displayName || r.username}</p>
                  <button onClick={decide(uid, "approve")} className="rounded-full bg-crimson px-3.5 py-1.5 text-xs font-bold text-white hover:bg-crimson-hover" data-testid={`approve-${uid}`}>Approve</button>
                  <button onClick={decide(uid, "decline")} className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-bold text-slate-300 hover:border-crimson/50" data-testid={`decline-${uid}`}>Decline</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showMembers && (
        <div className="mt-5">
          <p className="mb-2 font-heading text-sm font-bold text-white">Members · {conv.memberCount || members.length}</p>
          <div className="space-y-1" data-testid="group-members-list">
            {members.map((m) => {
              const isAdminMember = (conv.admins || []).includes(m.id) || conv.createdBy === m.id;
              const isMe = m.id === myId;
              return (
                <div key={m.id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/[0.04]" data-testid={`member-${m.id}`}>
                  <Avatar user={m} size={34} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">
                      {m.displayName || m.username} {isMe && <span className="text-slate-500">(you)</span>}
                    </p>
                    {isAdminMember && (
                      <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-crimson">
                        <Crown size={9} /> Admin
                      </p>
                    )}
                  </div>
                  {iAmAdmin && !isMe && (
                    <div className="flex gap-1.5">
                      <button
                        onClick={isAdminMember ? () => api.del(`/conversations/${conv.id}/admins/${m.id}`).then(onChanged).catch((e) => setError(e.message)) : call(`/conversations/${conv.id}/admins/${m.id}`)}
                        title={isAdminMember ? "Remove admin" : "Make admin"}
                        className="rounded-full border border-white/15 p-2 text-slate-300 hover:border-crimson/50 hover:text-white"
                        data-testid={`toggle-admin-${m.id}`}
                      >
                        {isAdminMember ? <ShieldOff size={13} /> : <Shield size={13} />}
                      </button>
                      <button
                        onClick={() => api.del(`/conversations/${conv.id}/members/${m.id}`).then(onChanged).catch((e) => setError(e.message))}
                        title="Remove from group"
                        className="rounded-full border border-white/15 p-2 text-slate-300 hover:border-crimson/50 hover:text-crimson"
                        data-testid={`remove-member-${m.id}`}
                      >
                        <UserMinus size={13} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {iAmAdmin && (
            <div className="mt-3">
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5">
                <UserPlus size={15} className="text-slate-500" />
                <input value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="Search people to add…" className="w-full bg-transparent text-sm text-white outline-none placeholder-slate-600" data-testid="add-member-search" />
              </div>
              {addResults.length > 0 && (
                <div className="mt-2 space-y-1">
                  {addResults.filter((u) => !(conv.memberIds || []).includes(u.id)).map((u) => (
                    <div key={u.id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/[0.04]">
                      <Avatar user={u} size={30} />
                      <p className="min-w-0 flex-1 truncate text-sm text-white">{u.displayName || u.username}</p>
                      <button onClick={call(`/conversations/${conv.id}/members`, { userId: u.id })} className="rounded-full bg-crimson px-3.5 py-1.5 text-xs font-bold text-white hover:bg-crimson-hover" data-testid={`add-to-group-${u.id}`}>
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {!showMembers && <p className="mt-5 text-center text-xs text-slate-600">The member list is hidden by the admins.</p>}

      {error && <p className="mt-4 text-sm text-crimson" data-testid="group-settings-error">{error}</p>}

      <button onClick={call(`/conversations/${conv.id}/leave`)} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-crimson/40 py-3 font-heading text-sm font-bold text-crimson transition-colors hover:bg-crimson/10" data-testid="leave-group-button">
        <LogOut size={15} /> Leave group
      </button>
    </Modal>
  );
};
